import React from 'react'

const GameScreen = ({ 
  player, 
  currentRound, 
  selectedAnswers, 
  allAnswersSubmitted, 
  answersSubmittedToServer, 
  onAnswerSelect, 
  onSubmitAnswers 
}) => {
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Game header */}
        <div className="card-retro p-6 mb-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-6">
              <div className="text-4xl">{player.identity}</div>
              <div className="text-purgatory-mist text-xl font-bold">VS</div>
              <div className="text-4xl">{currentRound.opponentIdentity}</div>
            </div>
          </div>
        </div>

        {/* Timer */}
        <div className="card-retro p-4 mb-6 text-center">
          <div className={`text-4xl font-bold font-mono ${
            currentRound.timeRemaining <= 10 ? 'text-purgatory-ember animate-pulse' : 'text-neon-green'
          }`}>
            {formatTime(currentRound.timeRemaining)}
          </div>
          <div className="text-sm text-purgatory-mist">
            Round {currentRound.roundNumber} • {answersSubmittedToServer ? 'Waiting for opponent...' : 'Choose your answers'}
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {currentRound.questions.map((question, questionIndex) => (
            <div key={questionIndex} className="card-retro p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purgatory-gold text-purgatory-dark rounded-full flex items-center justify-center font-bold text-sm">
                  {questionIndex + 1}
                </div>
                <div className="flex-1">
                  <div className="text-xs text-purgatory-mist mb-2 uppercase tracking-wider">
                    {question.theme}
                  </div>
                  <h3 className="text-lg font-medium text-purgatory-light">
                    {question.text}
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {question.options.map((option, optionIndex) => (
                  <button
                    key={optionIndex}
                    onClick={() => onAnswerSelect(questionIndex, optionIndex)}
                    disabled={currentRound.timeRemaining <= 0 || answersSubmittedToServer}
                    className={`p-4 text-left border-2 rounded transition-all duration-200 ${
                      selectedAnswers[questionIndex] === optionIndex
                        ? 'border-neon-green bg-neon-green bg-opacity-20 text-neon-green'
                        : 'border-purgatory-ash hover:border-purgatory-mist text-purgatory-light hover:bg-purgatory-ash hover:bg-opacity-20'
                    } ${(currentRound.timeRemaining <= 0 || answersSubmittedToServer) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs ${
                        selectedAnswers[questionIndex] === optionIndex
                          ? 'border-neon-green bg-neon-green text-purgatory-dark'
                          : 'border-purgatory-mist'
                      }`}>
                        {String.fromCharCode(65 + optionIndex)}
                      </div>
                      <span className="text-sm">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Progress indicator */}
        <div className="mt-6 text-center">
          <div className="inline-flex space-x-2">
            {currentRound.questions.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${
                  selectedAnswers[index] !== undefined
                    ? 'bg-neon-green'
                    : 'bg-purgatory-ash'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-purgatory-mist mt-2">
            {Object.keys(selectedAnswers).length} of {currentRound.questions.length} answered
          </p>
        </div>

        {/* Submit button */}
        <div className="mt-6 text-center">
          {answersSubmittedToServer ? (
            <div className="card-retro p-4 bg-purgatory-ash bg-opacity-20">
              <div className="text-purgatory-gold text-lg font-bold mb-2">
                ⏳ Answers Submitted
              </div>
              <div className="text-sm text-purgatory-mist">
                Waiting for your partner to finish...
              </div>
            </div>
          ) : allAnswersSubmitted ? (
            <button 
              onClick={onSubmitAnswers}
              className="btn-primary w-full max-w-md text-lg py-4 bg-neon-green text-purgatory-dark border-2 border-neon-green animate-pulse"
            >
              🚀 SUBMIT ANSWERS
            </button>
          ) : (
            <div className="card-retro p-4 bg-purgatory-ash bg-opacity-10">
              <div className="text-purgatory-mist text-sm">
                Answer all questions to submit
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GameScreen
